import { inImperial } from "./units.js"

const _apiBase =
  process.env.REACT_APP_APIBASE || `${window.location.origin}/api`;

const _transformActivities = (activity) => {
  if (activity) {
  // console.log(`_transformActivities - activity : ${activity}`);
    return {
      id: activity.id,
      title: activity.title,
      description: activity.description,
      trackId: activity.trail_id,
      uuid: activity.uuid,
      share_status: activity.share_status,
      share_type: activity.share_type,
      waypoints: activity.waypoints,
    };
  }
  // console.log("_transformActivities - empty");
  return null;
};

const _transformWaypoints = (wpt) => {
  if (wpt) {
    return {
      id: wpt.id,
      index: wpt.index,
      lat: wpt.lat,
      lon: wpt.lon,
      gradeDownHill: wpt.sec_avg_dh_grd,
      gradeUpHill: wpt.sec_avg_uph_grd,
      elevGain: wpt.sec_elev_gain,
      elevLoss: wpt.sec_elev_loss,
      type: wpt.sec_type,
      title: wpt.title,
      distance: wpt.distance,
      secDist: wpt.sec_dist,
      time: wpt.sec_time,
      pace: wpt.sec_pace,
      description: wpt.description,
      elapsedTime: wpt.elapsed_time,
    };
  }
  // console.log("_transformWaypoints - empty");
  return null;
};
const getStripeLevel = () => {
  return fetch(`${_apiBase}/user/stripe_level`).then((res) => res.json(res));
};
const getUserEmail = () => {
  try {
    return fetch(`${_apiBase}/user/email`).then((res) => res.json(res));    
  } catch (error) {
    console.log("debug: user not logged in, can't find email");
    return null;
  }
};

const checkSharedActivityOwner = async (id) => {
  let activityOwner = false;
  try {
    activityOwner = fetch(`${_apiBase}/shared_activity/${id}/owner`).then((res) => res.json(res));
    return activityOwner;
  }
  catch(error) {
    console.log(error);
    return activityOwner;
  }
};

const getAllActivities = async () => {
  return await fetch(`${_apiBase}/activities`)
    .then((res) => res.json(res))
    .then((res) => res.map(_transformActivities));
};

//TODO: Do we need two of these? One with uuid and one with id
const getActivity = async (id) => {
  return await fetch(`${_apiBase}/activity/uuid/${id}`)
    .then(async (res) => await res.json(res))
    .catch((error) => {
      console.log(`we caught an error in getActivity + ${error}`);
      return null;
    })
    .then((res) => _transformActivities(res));
};

const downloadGpx = (id) => {
  return `${_apiBase}/activity/${id}/gpx_file`;
};

const downloadSharedGpx = (uuid) => {
  return `${_apiBase}/shared_activity/${uuid}/gpx_file`;
};

const getSharedActivity = async (uuid) => {
  return await fetch(`${_apiBase}/shared_activity/${uuid}/details`)
    .then((res) => res.json(res))
    .then((res) => _transformActivities(res));
};

const getSharingUrl = async (id) => {
  return await fetch(`${_apiBase}/activity/${id}/create_public_url`);
};

const querySharingUrl = async (id) => {
  return await fetch(`${_apiBase}/activity/${id}/public_url`);
};

const deleteActivity = async (id) => {
  return await fetch(`${_apiBase}/activity/${id}`, {
    method: "DELETE",
  });
};

const resetActivity = async (id) => {
  return await fetch(`${_apiBase}/activity/${id}/reset`);
};

const duplicateActivity = async (id) => {
  return await fetch(`${_apiBase}/duplicate_activity/${id}`, {
    method: "POST",
  });
};

const getAllTrails = async () => {
  try {
    return await fetch(`${_apiBase}/trails`).then((res) => res.json(res));    
  } catch (error) {
    console.log("Error loading getAllTrails");
    return null;
  }
};

const getAllWaypoints = async (id) => {
  try {
    return await fetch(`${_apiBase}/activity/${id}/waypoints`)
    .then((res) => res.json(res))
    .then((res) => res.map(_transformWaypoints));    
  } catch (error) {
    console.log("Error loading getAllWaypoints");
    return null;
  }
};

const addWaypoint = async (id, index) => {
  const formData = new FormData();
  formData.append("index", index);
  return await fetch(`${_apiBase}/activity/${id}/add_waypoint`, {
    method: "POST",
    body: formData,
  });
};

const deleteWaypoint = async (id) => {
  return await fetch(`${_apiBase}/waypoint/${id}`, {
    method: "DELETE",
  });
};

const moveWaypoint = async (id, index, title, description, time = null) => {
  const formData = new FormData();
  formData.append("index", index);
  formData.append("title", title);
  formData.append("description", description);
  if (time) {
    formData.append("elapsed_time", time);
  }
  return await fetch(`${_apiBase}/waypoint/${id}`, {
    method: "PATCH",
    body: formData,
  });
};

const addActivity = async (
  title,
  description,
  selectedTrack,
  gpxFile,
  time_opt_type,
  time_opt_value,
  preferredUnit,
  smooth_elev_option,
) => {
  const formData = new FormData();
  let time_opt_value_adj = time_opt_value;

  if (inImperial() && time_opt_type === 'pace') {
    let hms = time_opt_value.split(':'); // split it at the colons
    // minutes are worth 60 seconds. Hours are worth 60 minutes.
    let total_seconds = (+hms[0]) * 60 * 60 + (+hms[1]) * 60 + (+hms[2]);
    let total_seconds_adj = 0.621371 * total_seconds;
    time_opt_value_adj = new Date(total_seconds_adj * 1000).toISOString().substring(11, 19);
  }
  formData.append("title", title);
  formData.append("description", description);
  formData.append("selectedTrack", selectedTrack);
  formData.append("file", gpxFile);
  formData.append("time_opt_type", time_opt_type);
  formData.append("time_opt_value", time_opt_value_adj);
  formData.append("preferred_unit", preferredUnit);
  formData.append("smooth_elev_option", smooth_elev_option);

  return await fetch(`${_apiBase}/add_activity`, {
    method: "POST",
    body: formData,
  });
};

const updateActivity = async (id, title, description) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("descr", description);

  const response = await fetch(`${_apiBase}/activity/${id}`, {
    method: "PATCH",
    body: formData,
  });
  return response;
};

const updateActivityUuid = async (uuid, title, description) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("descr", description);

  const response = await fetch(`${_apiBase}/activity/uuid/${uuid}`, {
    method: "PATCH",
    body: formData,
  });
  return response;
};

export async function getTrack(id) {
  try {
    if (!id) {
      throw new Error("error");
    }

    return await fetch(`${_apiBase}/${id}/track`).then((response) =>
      response.text()
    );
  } catch (e) {}
}

export async function getSharedTrack(id) {
  try {
    if (!id) {
      throw new Error("error");
    }

    // await timeout(2000);

    return await fetch(`${_apiBase}/shared_activity/${id}/track`).then((response) =>
      response.text()
    );
  } catch (error) {
    console.log(`Error - getSharedTrack: ${error}`);
  }
}

// It's similar to addActivity but we decide for a separate version since it is so central
const addActivityCopy = async (
  title,
  time_opt_type,
  time_opt_value,
  preferredUnit,
  original_activity_uuid,
) => {
  const formData = new FormData();
  let time_opt_value_adj = time_opt_value;

  if (inImperial() && time_opt_type === 'pace') {
    let hms = time_opt_value.split(':'); // split it at the colons
    // minutes are worth 60 seconds. Hours are worth 60 minutes.
    let total_seconds = (+hms[0]) * 60 * 60 + (+hms[1]) * 60 + (+hms[2]);
    let total_seconds_adj = 0.621371 * total_seconds;
    time_opt_value_adj = new Date(total_seconds_adj * 1000).toISOString().substring(11, 19);
  }
  formData.append("title", title);
  formData.append("time_opt_type", time_opt_type);
  formData.append("time_opt_value", time_opt_value_adj);
  formData.append("preferred_unit", preferredUnit);
  formData.append("original_activity_uuid", original_activity_uuid);

  return await fetch(`${_apiBase}/add_activity_copy`, {
    method: "POST",
    body: formData,
  });
};


export {
  getAllActivities,
  getActivity,
  getSharedActivity,
  getSharingUrl,
  querySharingUrl,
  addActivity,
  addActivityCopy,
  updateActivity,
  updateActivityUuid,
  deleteActivity,
  resetActivity,
  duplicateActivity,
  getAllTrails,
  getAllWaypoints,
  addWaypoint,
  deleteWaypoint,
  moveWaypoint,
  getStripeLevel,
  getUserEmail,
  checkSharedActivityOwner,
  downloadGpx,
  downloadSharedGpx,
};
